import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  applyPlaybookTemplate,
  archivePlaybookTemplate,
  createPlaybookTemplate,
  editPlaybookTemplate,
  fetchAllPlaybookTemplates,
  fetchPlaybookTemplateById,
  fetchPlaybookTemplateChangelog,
  fetchPlaybookTaskTemplateChangelog,
  setActivePlaybookTemplate,
  setInactivePlaybookTemplate,
} from '../../routines/playbookTemplateRoutines';
import { fetchAllPlaybookInstances } from '../../routines/playbookInstanceRoutines';

const PLAYBOOK_TEMPLATE_URL = `${envConfig.APIG_URL}playbook/playbook-templates`;
const PM_URL = `${envConfig.APIG_URL}project-mgt/playbook-templates`;

export function* fetchAllPlaybookTemplatesSaga() {
  try {
    const response = yield call(request, PLAYBOOK_TEMPLATE_URL);
    yield put(fetchAllPlaybookTemplates.success(response));
  } catch (error) {
    yield put(fetchAllPlaybookTemplates.failure({ error }));
  } finally {
    yield put(fetchAllPlaybookTemplates.fulfill());
  }
}

export function* fetchPlaybookTemplatesByIdSaga({ payload }) {
  try {
    const response = yield call(request, `${PM_URL}/${payload.templateId}`);
    yield put(fetchPlaybookTemplateById.success(response));
  } catch (error) {
    yield put(fetchPlaybookTemplateById.failure({ error, noAlert: true }));
  } finally {
    yield put(fetchPlaybookTemplateById.fulfill());
  }
}

export function* fetchPlaybookTemplateChangeLogByIdSaga({ payload }) {
  const { associatedItemId: templateId } = payload;
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_TEMPLATE_URL}/${templateId}/changelog`
    );
    yield put(fetchPlaybookTemplateChangelog.success(response));
  } catch (error) {
    yield put(fetchPlaybookTemplateChangelog.failure({ error }));
  } finally {
    yield put(fetchPlaybookTemplateChangelog.fulfill());
  }
}

export function* fetchPlaybookTaskTemplateChangeLogByIdSaga({ payload }) {
  const { associatedItemId: taskTemplateId } = payload;
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_TEMPLATE_URL}/task-templates/${taskTemplateId}/changelog`
    );
    yield put(fetchPlaybookTaskTemplateChangelog.success(response));
  } catch (error) {
    yield put(fetchPlaybookTaskTemplateChangelog.failure({ error }));
  } finally {
    yield put(fetchPlaybookTaskTemplateChangelog.fulfill());
  }
}

export function* archivePlaybookTemplateSaga({ payload }) {
  try {
    const { playbookTemplateId, option: action } = payload;
    const response = yield call(
      request,
      `${PLAYBOOK_TEMPLATE_URL}/${playbookTemplateId}/archive`,
      {
        method: 'POST',
        payload: {
          action,
        },
      }
    );
    yield put(
      archivePlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
    yield put(fetchAllPlaybookInstances.trigger());
  } catch (error) {
    yield put(archivePlaybookTemplate.failure({ error }));
  } finally {
    yield put(archivePlaybookTemplate.fulfill());
  }
}

export function* applyPlaybookTemplateSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PM_URL}/${payload.templateId}/apply`,
      {
        method: 'POST',
        payload: { entities: payload.entities },
      }
    );
    yield put(
      applyPlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.APPLY_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
    yield put(fetchAllPlaybookInstances.trigger());
  } catch (error) {
    yield put(applyPlaybookTemplate.failure({ error }));
  } finally {
    yield put(applyPlaybookTemplate.fulfill());
  }
}

export function* setInactivePlaybookTemplateSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_TEMPLATE_URL}/${payload}/inactive`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      setInactivePlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.SET_INACTIVE_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(setInactivePlaybookTemplate.failure({ error }));
  } finally {
    yield put(setInactivePlaybookTemplate.fulfill());
  }
}

export function* setActivePlaybookTemplateSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_TEMPLATE_URL}/${payload}/active`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      setActivePlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.SET_ACTIVE_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(setActivePlaybookTemplate.failure({ error }));
  } finally {
    yield put(setActivePlaybookTemplate.fulfill());
  }
}

export function* createPlaybookTemplateSaga({ payload }) {
  try {
    const { formData } = payload;
    const response = yield call(request, `${PM_URL}`, {
      method: 'POST',
      payload: formData,
    });
    yield put(
      createPlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.CREATE_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(createPlaybookTemplate.failure({ error }));
  } finally {
    yield put(createPlaybookTemplate.fulfill());
  }
}

export function* editPlaybookTemplateSaga({ payload }) {
  try {
    const { formData } = payload;
    const response = yield call(request, `${PM_URL}/${payload.templateId}`, {
      method: 'PUT',
      payload: formData,
    });
    yield put(
      editPlaybookTemplate.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_PLAYBOOK_TEMPLATE,
      })
    );
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(editPlaybookTemplate.failure({ error }));
  } finally {
    yield put(editPlaybookTemplate.fulfill());
  }
}
export async function validatePlaybookNameExist({ entity, name }) {
  let URL = `${PLAYBOOK_TEMPLATE_URL}/search?entity=${entity}`;
  if (name) {
    URL = `${URL}&name=${name}`;
  } else {
    throw new Error('No Value Entered!');
  }
  try {
    return await request(URL);
  } catch (error) {
    return null;
  }
}

export default function* root() {
  yield all([
    takeLatest(
      fetchAllPlaybookTemplates.TRIGGER,
      fetchAllPlaybookTemplatesSaga
    ),
    takeLatest(
      fetchPlaybookTemplateById.TRIGGER,
      fetchPlaybookTemplatesByIdSaga
    ),
    takeLatest(
      fetchPlaybookTemplateChangelog.TRIGGER,
      fetchPlaybookTemplateChangeLogByIdSaga
    ),
    takeLatest(
      fetchPlaybookTaskTemplateChangelog.TRIGGER,
      fetchPlaybookTaskTemplateChangeLogByIdSaga
    ),
    takeEvery(archivePlaybookTemplate.TRIGGER, archivePlaybookTemplateSaga),
    takeEvery(
      setInactivePlaybookTemplate.TRIGGER,
      setInactivePlaybookTemplateSaga
    ),
    takeEvery(setActivePlaybookTemplate.TRIGGER, setActivePlaybookTemplateSaga),
    takeEvery(createPlaybookTemplate.TRIGGER, createPlaybookTemplateSaga),
    takeEvery(editPlaybookTemplate.TRIGGER, editPlaybookTemplateSaga),
    takeEvery(applyPlaybookTemplate.TRIGGER, applyPlaybookTemplateSaga),
  ]);
}
